import { Injectable } from '@angular/core';
import { PosOrder } from 'src/app/models/pos-order.model';

@Injectable({ providedIn: 'root' })
export class PosPrintService {
  // If you run a local print server, set its base URL here (http://localhost:4000 default)
  private localPrintServer = 'http://localhost:4000';

  // Public API: print order to two printers (or as configured)
  async printOrderCopies(order: PosOrder, copies = 2): Promise<void> {
    const html = this.generateReceiptHtml(order);

    // Try local print server first (recommended for reliability)
    try {
      await this.printViaLocalServer(html, copies);
      return;
    } catch (err) {
      console.warn('Local print server failed or unreachable, falling back to browser print', err);
    }

    // Fallback: open print window copy times copies
    for (let i = 0; i < copies; i++) {
      await this.browserPrint(html);
    }
  }

  private async printViaLocalServer(html: string, copies: number) {
    // expected local server endpoint: POST /print
    // payload: { html: string, copies: number }
    const res = await fetch(`${this.localPrintServer}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html, copies })
    });

    if (!res.ok) throw new Error('Local print server responded with ' + res.status);
    return;
  }

  private async browserPrint(html: string) {
    return new Promise<void>((resolve) => {
      const w = window.open('', '_blank', 'width=400,height=600');
      if (!w) {
        console.error('Popup blocked for printing fallback');
        resolve();
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
      // some printers require a slight delay before print
      setTimeout(() => {
        w.print();
        w.close();
        resolve();
      }, 300);
    });
  }

  private generateReceiptHtml(order: PosOrder): string {
    const itemsHtml = order.items
      .map(
        (i) =>
          `<tr>
             <td style="padding:2px 4px;">${this.escape(i.itemName)}</td>
             <td style="padding:2px 4px;text-align:center;">${i.qty}</td>
             <td style="padding:2px 4px;text-align:right;">${i.rate.toFixed(2)}</td>
             <td style="padding:2px 4px;text-align:right;">${i.total.toFixed(2)}</td>
           </tr>`
      )
      .join('');
    return `
      <div style="font-family: monospace; width: 280px;">
        <h3 style="text-align:center; margin:6px 0;">TechElevate</h3>
        <div>Date: ${new Date(order.orderDate).toLocaleString()}</div>
        <div>Order: ${order.localId}</div>
        <table style="width:100%; border-collapse: collapse; margin-top:6px;">
          <thead>
            <tr><th style="text-align:left">Item</th><th>Q</th><th style="text-align:right">Rate</th><th style="text-align:right">Total</th></tr>
          </thead>
          <tbody>${itemsHtml}</tbody>
        </table>
        <hr/>
        <div style="display:flex; justify-content:space-between;"><div>Subtotal</div><div>${order.subTotal.toFixed(2)}</div></div>
        <div style="display:flex; justify-content:space-between;"><div>Tax</div><div>${order.totalTax.toFixed(2)}</div></div>
        <div style="display:flex; justify-content:space-between; font-weight:bold;"><div>Total</div><div>${order.grandTotal.toFixed(2)}</div></div>
        <div>Payment: ${order.paymentMode ?? 'N/A'}</div>
        <div style="text-align:center; margin-top:8px;">*** Customer Copy ***</div>
      </div>`;
  }

  private escape(s: string) {
    return (s || '').replace(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m] as string));
  }
}
